import { useMeasure } from '@jouzen/ecom-components/hooks/useMeasure';
import { cx } from 'class-variance-authority';
import { useTranslations } from 'next-intl';
import { type RefObject, useRef } from 'react';
import type { MessageKey } from 'types/LocaleMessageKey';

import Image from '@/app/components/Image';
import Motion from '@/app/components/Motion';

import { getCarouselNavButtonLabelMotionProps } from './utils/getCarouselNavButtonLabelMotionProps';

export interface PopFinishesCarouselNavButtonProps {
  readonly image: string;
  readonly index: number;
  readonly isActive: boolean;
  readonly isInView: boolean;
  readonly label: MessageKey;
  readonly onChange: (activeIndex: number) => void;
}

const PopFinishesCarouselNavButton = ({
  image,
  index,
  isActive,
  isInView,
  label,
  onChange,
}: PopFinishesCarouselNavButtonProps) => {
  const t = useTranslations();
  const labelRef = useRef<HTMLSpanElement>(null);
  const { width: labelWidth } = useMeasure({
    ref: labelRef as RefObject<HTMLSpanElement>,
  });

  const handleClick = () => {
    onChange(index);
  };

  const labelMotionProps = getCarouselNavButtonLabelMotionProps({
    isActive,
    isInView,
    labelWidth,
  });

  return (
    <button
      className="flex"
      data-cy="pop-finishes-carousel-nav-button"
      onClick={handleClick}
    >
      <span className="flex size-10 items-center justify-center">
        <span
          className={cx(
            'box-border block flex size-7 items-center justify-center rounded-full border border-solid transition-colors duration-500',
            {
              'border-transparent': !isActive,
              'border-slate-700': isActive,
            },
          )}
          data-cy="pop-finishes-carousel-nav-button-swatch"
        >
          <Image
            alt=""
            className="block size-5 rounded-full"
            height={40}
            src={image}
            width={40}
          />
        </span>
      </span>
      <Motion
        className="relative block h-10 overflow-hidden"
        el="span"
        {...labelMotionProps}
      >
        <span
          className="absolute top-1/2 left-0 block -translate-y-1/2 pr-1 text-xs whitespace-nowrap lg:pr-3"
          ref={labelRef}
        >
          <span className="whitespace-nowrap max-md:sr-only">{t(label)}</span>
        </span>
      </Motion>
    </button>
  );
};

export default PopFinishesCarouselNavButton;
